import React, { useEffect, useState } from 'react';

import { Box, FormControl, Grid, MenuItem } from '@material-ui/core';
import moment from 'moment';

import { CustomDatePicker, CustomInputSelect, InputField } from 'src/components';
import { volunteerStatus } from 'src/pages/event-management/components/volunteer/types';
import { CustomLabel } from 'src/pages/signup/components/types';
import { CustomButtonStyled, eventCategory } from 'src/pages/user-management/types';
import { ISearchVolunteerAttendance } from 'src/services/volunteer';
import { shallowEqual } from 'src/utils/common';

import { defaultSearchVolunteer } from '../types';

const LEAST_CHAR_FOR_SEARCH = 3;

interface ISearchVolunteerView {
  _onSearch: (search: ISearchVolunteerAttendance) => void;
  _onReset: () => void;
  loading: boolean;
  searchData: ISearchVolunteerAttendance;
}

const SearchVolunteer = ({ searchData, loading, _onSearch, _onReset }: ISearchVolunteerView) => {
  const [search, setSearch] = useState<ISearchVolunteerAttendance>(defaultSearchVolunteer);

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch({ ...search, [e.target.name]: e.target.value === 'all' ? '' : e.target.value });
  }

  const onBlurField = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
    const valueTrimmed = e.target.value.trim();
    setSearch({ ...search, [e.target.name]: valueTrimmed });
  };

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      setSearch({ ...search, fromDate: date });
    }
  };
  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setSearch({ ...search, toDate: date });
    }
  };

  const reset = () => {
    _onReset();
    setSearch(defaultSearchVolunteer);
  };

  useEffect(() => {
    if (!shallowEqual(search, searchData)) {
      setSearch(searchData);
    }
  }, []);

  return (
    <Box mb={4}>
      <Grid container justifyContent="space-between">
        <Grid item xs={8}>
          <Box display="flex" justifyContent="space-between">
            <Box flex={1} pr={2}>
              <InputField
                title="Event Name"
                placeholder="Enter at least 3 characters"
                name="eventName"
                value={search.eventName}
                onChange={_handleChange}
                onBlur={onBlurField}
                margin="dense"
                fullWidth
                InputProps={{
                  inputProps: {
                    maxLength: 50,
                  },
                }}
                error={search.eventName !== '' && search.eventName.length < LEAST_CHAR_FOR_SEARCH}
                errorMessage="Enter at least 3 characters"
              />
            </Box>
            <Box flex={1} pr={2}>
              <InputField
                title="Volunteer"
                placeholder="Enter at least 3 characters"
                name="volunteer"
                value={search.volunteer}
                onChange={_handleChange}
                onBlur={onBlurField}
                margin="dense"
                fullWidth
                notice
                noticeTitle={'You can search by NSAA ID, Name, Email Address or Phone number'}
                noticeYellow
                InputProps={{
                  inputProps: {
                    maxLength: 50,
                  },
                }}
                error={search.volunteer !== '' && search.volunteer.length < LEAST_CHAR_FOR_SEARCH}
                errorMessage="Enter at least 3 characters"
              />
            </Box>
            <Box flex={1}>
              <CustomInputSelect
                title="Volunteer Status"
                name="volunteerStatus"
                value={search.volunteerStatus === '' ? 'all' : search.volunteerStatus}
                onChange={_handleChange}
                margin="dense"
                fullWidth
                notRequired
              >
                <MenuItem key="all" value="all">
                  All
                </MenuItem>
                {volunteerStatus.map((item) => (
                  <MenuItem key={item.key} value={item.key}>
                    {item.value}
                  </MenuItem>
                ))}
              </CustomInputSelect>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Box flex={1} pr={2}>
              <CustomInputSelect
                title="Event Category"
                name="eventCategory"
                value={search.eventCategory === '' ? 'all' : search.eventCategory}
                onChange={_handleChange}
                margin="dense"
                fullWidth
                notRequired
              >
                <MenuItem key="all" value="all">
                  All
                </MenuItem>
                {eventCategory.map((item) => (
                  <MenuItem key={item.key} value={item.key}>
                    {item.value}
                  </MenuItem>
                ))}
              </CustomInputSelect>
            </Box>
            <Box flex={1} pr={2} display="flex" mt={3}>
              <FormControl fullWidth>
                <CustomLabel>From Date</CustomLabel>
                <CustomDatePicker
                  onChange={handleFromDateChange}
                  value={search.fromDate ? new Date(search.fromDate) : null}
                  name="fromDate"
                  maxDate={search.toDate ? moment(search.toDate).subtract(0, 'years') : moment().subtract(0, 'years')}
                />
              </FormControl>
            </Box>
            <Box flex={1} display="flex" mt={3}>
              <FormControl fullWidth>
                <CustomLabel>To Date</CustomLabel>
                <CustomDatePicker
                  onChange={handleToDateChange}
                  value={search.toDate ? new Date(search.toDate) : null}
                  name="toDate"
                  minDate={moment(search?.fromDate).subtract(0, 'years')}
                />
              </FormControl>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end" mt={6}>
            {!shallowEqual(searchData, defaultSearchVolunteer) && (
              <Box display="flex">
                <CustomButtonStyled disabled={loading} outlineButton onClick={reset}>
                  Reset
                </CustomButtonStyled>
              </Box>
            )}
            <Box display="flex">
              <CustomButtonStyled
                disabled={
                  loading ||
                  (search.eventName !== '' && search.eventName.length < LEAST_CHAR_FOR_SEARCH) ||
                  (search.volunteer !== '' && search.volunteer.length < LEAST_CHAR_FOR_SEARCH)
                }
                onClick={_onSearch(search)}
              >
                Search
              </CustomButtonStyled>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchVolunteer;
